/**
 * Fetches the rows buildOwnerEmailHistory needs, scoped to one owner's own
 * apartments — never portfolio-wide, matching the spec's own instruction
 * that every owner-lifecycle mail is about that owner's activity only.
 *
 * "Sent" evidence comes exclusively from owner_lifecycle_emails (20260826160000),
 * a real send-log — never from the *_owner_email_sent_at stamp columns, which
 * are an idempotency claim (set before the muted-preference check) and, for
 * anything predating the feature, a historical proxy backfilled by
 * 20260826150000. The business-event columns below (deposit paid_at,
 * handover_protocols.submitted_at, etc.) are the same real proxies that
 * backfill used, queried live instead of trusting a stamp column, purely to
 * decide whether a row should render at all (an event that happened, mail or
 * not) — never to decide whether a mail was sent.
 *
 * A failed send-log fetch is the one thing here that is NOT degraded to an
 * empty list — see the comment at the throw. Without it the card would show a
 * confident list of "Pending" rows for mails that may well have gone out.
 *
 * Rent is gated on the settled `payments` rows rather than on the log — see
 * the header of src/lib/owner-email-history.ts for why — which is why this
 * hook also loads paid rent payments and resolves `marked_paid_by` to a name.
 *
 * Kept separate from useOwnerDetail (which already loads apartments for the
 * KPI strip and the Apartments tab) rather than folding the extra columns
 * into that 1800-line hook: this data is only for one card, on one tab, and
 * useOwnerDetail already degrades individual enrichments independently — one
 * more query failing there would mean chasing it through an already large
 * function.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  buildOwnerEmailHistory,
  type OwnerEmailHistoryEntry,
} from "@/lib/owner-email-history";

export function useOwnerEmailHistory(
  lessorId: string | null | undefined,
  apartmentIds: Array<number | null>
) {
  const ids = Array.from(
    new Set(apartmentIds.filter((id): id is number => id != null))
  );

  return useQuery<OwnerEmailHistoryEntry[]>({
    queryKey: ["owner-email-history", lessorId, ids.slice().sort()],
    enabled: !!lessorId && ids.length > 0,
    queryFn: async () => {
      const [apartmentsRes, bookingsRes, invoicesRes] = await Promise.all([
        supabase.from("apartments").select("id, label").in("id", ids),
        supabase
          .from("bookings")
          .select(
            "id, name, contract_signed_at, key_picked_up_at, move_out_date, " +
              "key_returned_at, rooms!inner(apartment_id, apartments(label))"
          )
          .in("rooms.apartment_id", ids),
        supabase
          .from("management_invoices")
          .select("invoice_no, period_month, sent_at")
          .eq("lessor_id", lessorId)
          .not("sent_at", "is", null)
          .order("sent_at", { ascending: false })
          .limit(12),
      ]);
      if (apartmentsRes.error) throw apartmentsRes.error;
      // Bookings/invoices are enrichment on top of the apartment list — a
      // failure here degrades the card to "fewer rows", not "broken card".
      const apartments = apartmentsRes.data ?? [];
      const bookingRows: any[] = bookingsRes.error
        ? []
        : (bookingsRes.data ?? []);
      const invoices = invoicesRes.error ? [] : (invoicesRes.data ?? []);

      const bookingIds = bookingRows.map((b) => b.id);
      const [
        settlementsRes,
        depositPaymentsRes,
        protocolsRes,
        sentLogsRes,
        rentPaymentsRes,
      ] = bookingIds.length
        ? await Promise.all([
            supabase
              .from("deposit_settlements")
              .select(
                "bundle_sent_at, review_reminded_at, statement_sent_at, refund_paid_at, " +
                  "bookings(name)"
              )
              .in("booking_id", bookingIds),
            supabase
              .from("payments")
              .select("booking_id, paid_at")
              .eq("type", "deposit")
              .eq("status", "paid")
              .in("booking_id", bookingIds),
            supabase
              .from("handover_protocols")
              .select("booking_id, kind, submitted_at")
              .eq("status", "submitted")
              .in("booking_id", bookingIds),
            supabase
              .from("owner_lifecycle_emails")
              .select(
                "email_type, sent_at, booking_id, apartment_id, payment_id, " +
                  "outcome, skip_reason"
              )
              .or(
                `apartment_id.in.(${ids.join(",")}),booking_id.in.(${bookingIds.join(",")})`
              ),
            // The per-month rent event. Fetched deeper than the card's own
            // RENT_RECEIVED_LIMIT so the twelve it renders are the twelve
            // most recent by settlement date, not by due date — ordered by
            // `paid_at`, not `due_date`: buildOwnerEmailHistory's own cap
            // sorts candidates by marked_paid_at ?? paid_at ?? due_date, so a
            // due_date-ordered fetch could drop a just-settled OLD period
            // behind 24 newer-due-date rows before the builder ever saw it.
            // `paid_at` is set for every paid row (Stripe or admin-attested)
            // and lands within moments of marked_paid_at when both are set,
            // so it tracks that sort key closely enough to fetch the right 24.
            supabase
              .from("payments")
              .select(
                "id, booking_id, due_date, paid_at, marked_paid_at, marked_paid_by"
              )
              .eq("type", "rent")
              .eq("status", "paid")
              .in("booking_id", bookingIds)
              .order("paid_at", { ascending: false, nullsFirst: false })
              .limit(24),
          ])
        : [
            { data: [], error: null },
            { data: [], error: null },
            { data: [], error: null },
            await supabase
              .from("owner_lifecycle_emails")
              .select(
                "email_type, sent_at, booking_id, apartment_id, payment_id, " +
                  "outcome, skip_reason"
              )
              .in("apartment_id", ids),
            // No bookings means no tenancies, so no rent either.
            { data: [], error: null },
          ];
      // The send-log is the ONE query that cannot degrade to []: every "sent"
      // flag comes from it, so an empty fallback would render every entry as
      // "Pending" — indistinguishable from a genuinely empty log, and a lie in
      // the opposite direction from the fabricated "Sent" entries this card was
      // fixed to stop showing. Throw instead and let the card say it could not
      // read the log. Everything below it stays best-effort: those rows only
      // decide whether an entry renders, never whether it was sent.
      if (sentLogsRes.error) throw sentLogsRes.error;
      const settlements: any[] = settlementsRes.error
        ? []
        : (settlementsRes.data ?? []);
      const depositPayments: any[] = depositPaymentsRes.error
        ? []
        : (depositPaymentsRes.data ?? []);
      const protocols: any[] = protocolsRes.error
        ? []
        : (protocolsRes.data ?? []);
      const sentLogs: any[] = sentLogsRes.data ?? [];
      // Enrichment, same as the rows above: a failure here degrades rent back
      // to the legacy log-only rendering (buildOwnerEmailHistory treats an
      // absent list that way on purpose), never to a broken card.
      const rentPaymentRows: any[] = rentPaymentsRes.error
        ? []
        : (rentPaymentsRes.data ?? []);

      /**
       * Who marked each month paid. `profiles` is the only place an admin's
       * name lives; a lookup that fails leaves the entry saying an admin
       * attested it without naming them, which is the honest degradation —
       * see marked_paid_by_id in OwnerEmailHistoryRentPayment.
       */
      const adminIds = Array.from(
        new Set(
          rentPaymentRows
            .map((p) => p.marked_paid_by)
            .filter((v): v is string => !!v)
        )
      );
      const adminNameById = new Map<string, string>();
      if (adminIds.length) {
        const { data: adminRows, error: adminErr } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", adminIds);
        if (!adminErr) {
          for (const a of adminRows ?? []) {
            const label =
              String(a.name ?? "").trim() || String(a.email ?? "").trim();
            if (label) adminNameById.set(a.id, label);
          }
        }
      }

      const depositPaidAtByBooking = new Map<number, string>();
      for (const p of depositPayments) {
        if (!p.paid_at) continue;
        const existing = depositPaidAtByBooking.get(p.booking_id);
        if (!existing || p.paid_at > existing) {
          depositPaidAtByBooking.set(p.booking_id, p.paid_at);
        }
      }
      const protocolSubmittedAtByBooking = new Map<string, string>();
      for (const p of protocols) {
        if (!p.submitted_at) continue;
        const key = `${p.booking_id}:${p.kind}`;
        const existing = protocolSubmittedAtByBooking.get(key);
        if (!existing || p.submitted_at > existing) {
          protocolSubmittedAtByBooking.set(key, p.submitted_at);
        }
      }

      return buildOwnerEmailHistory({
        apartments: apartments.map((a: any) => ({
          id: a.id,
          label: a.label ?? null,
        })),
        bookings: bookingRows.map((b: any) => ({
          id: b.id,
          name: b.name ?? null,
          apartment_label: b.rooms?.apartments?.label ?? null,
          contract_signed_at: b.contract_signed_at ?? null,
          deposit_paid_at: depositPaidAtByBooking.get(b.id) ?? null,
          key_picked_up_at: b.key_picked_up_at ?? null,
          move_in_protocol_submitted_at:
            protocolSubmittedAtByBooking.get(`${b.id}:move_in`) ?? null,
          move_out_date: b.move_out_date ?? null,
          move_out_protocol_submitted_at:
            protocolSubmittedAtByBooking.get(`${b.id}:move_out`) ?? null,
          key_returned_at: b.key_returned_at ?? null,
        })),
        invoices: invoices.map((i: any) => ({
          invoice_no: i.invoice_no ?? null,
          period_month: i.period_month ?? null,
          sent_at: i.sent_at ?? null,
        })),
        settlements: settlements.map((s: any) => ({
          tenant_name: s.bookings?.name ?? null,
          bundle_sent_at: s.bundle_sent_at ?? null,
          review_reminded_at: s.review_reminded_at ?? null,
          statement_sent_at: s.statement_sent_at ?? null,
          refund_paid_at: s.refund_paid_at ?? null,
        })),
        /**
         * `outcome` and `skip_reason` are the whole point of 20260903140000
         * and were being selected and then dropped here — so every logged
         * NON-send (muted owner, no address on file, provider rejection) came
         * out of buildOwnerEmailHistory as a green "Sent" entry, since an
         * absent `outcome` means "pre-migration send" by design. That made the
         * card's entire not-delivered path dead code and reintroduced
         * 20260826160000's fabricated-"Sent" bug for the failure case.
         */
        sentLogs: sentLogs.map((l: any) => ({
          email_type: l.email_type,
          sent_at: l.sent_at,
          booking_id: l.booking_id ?? null,
          apartment_id: l.apartment_id ?? null,
          payment_id: l.payment_id ?? null,
          outcome: l.outcome ?? null,
          skip_reason: l.skip_reason ?? null,
        })),
        rentPayments: rentPaymentRows.map((p: any) => ({
          id: String(p.id),
          booking_id: p.booking_id,
          due_date: p.due_date ?? null,
          paid_at: p.paid_at ?? null,
          marked_paid_at: p.marked_paid_at ?? null,
          marked_paid_by_id: p.marked_paid_by ?? null,
          marked_paid_by_name: p.marked_paid_by
            ? (adminNameById.get(p.marked_paid_by) ?? null)
            : null,
        })),
      });
    },
  });
}
